import { DateTime } from "luxon";

/**
 * Number of days an order is considered "recent". Recent orders show precise
 * arriving/delivered dates; older orders collapse to simple status labels.
 */
const RECENT_ORDER_DAYS = 90;

export interface ShipmentDisplayInput {
  /** Authoritative carrier enum value from the shipment table (e.g. "Ups", "Other"). */
  carrierEnum?: string | null;
  /** Fulfillment status label for the shipment (e.g. "Shipped", "Delivered"). */
  status?: string | null;
  /** Collapsed delivered date (shipment table preferred over raw line item). */
  deliveryOn?: Date | string | null;
  /** Estimated arrival from carrier tracking. */
  estimatedDeliveryOn?: Date | string | null;
  /** Order placement date, used to evaluate the recent-order window. */
  orderDate?: Date | string | null;
  /** Injectable clock for deterministic evaluation; defaults to now. */
  now?: DateTime;
}

export interface ShipmentDisplay {
  /** Primary status word: "Shipping" or "Delivered". */
  statusText: string;
  /** Optional secondary line: "Arriving Est ..." or "Delivered on ...". */
  detailText?: string;
}

function toDateTime(value?: Date | string | null): DateTime | null {
  if (!value) return null;
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value, { zone: "utc" })
      : DateTime.fromISO(value, { zone: "utc" });
  return dt.isValid ? dt : null;
}

function isDeliveredStatus(status?: string | null): boolean {
  const normalized = (status ?? "").replace(/\s+/g, "").toLowerCase();
  return normalized === "delivered"
}

function isOtherCarrier(carrierEnum?: string | null): boolean {
  const normalized = (carrierEnum ?? "").trim().toLowerCase();
  return normalized === "" || normalized === "other";
}

function formatDate(value: DateTime | null): string | undefined {
  return value?.isValid ? value.toFormat("M/d/yyyy") : undefined;
}

/**
 * Resolves how a shipment's status should be displayed.
 *
 * - Carrier "Other" (or unknown): never show a delivered-on date, only the label.
 * - Known carriers within the last 90 days: show "Arriving Est ..." while in
 *   transit and "Delivered on ..." once delivered.
 * - Known carriers older than 90 days: collapse to "Shipping" / "Delivered".
 */
export function resolveShipmentDisplay(input: ShipmentDisplayInput): ShipmentDisplay {
  const delivered = isDeliveredStatus(input.status);
  const statusText = delivered ? "Delivered" : "Shipping";

  if (isOtherCarrier(input.carrierEnum)) {
    return { statusText };
  }

  const now = input.now ?? DateTime.now();
  const orderDt = toDateTime(input.orderDate);
  const withinRecentWindow =
    orderDt !== null && orderDt >= now.minus({ days: RECENT_ORDER_DAYS });

  if (!withinRecentWindow) {
    return { statusText };
  }

  if (delivered) {
    const detail = formatDate(toDateTime(input.deliveryOn));
    return {
      statusText,
      detailText: detail ? `Delivered on ${detail}` : undefined,
    };
  }

  const detail = formatDate(
    toDateTime(input.estimatedDeliveryOn ?? input.deliveryOn),
  );
  return {
    statusText,
    detailText: detail ? `Arriving Est ${detail}` : undefined,
  };
}




